import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { withdrawalApi, walletApi } from '../lib/api';
import { formatCurrency, mask } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Building, Smartphone, Plus, Check, Loader2, ChevronRight } from 'lucide-react';

const WithdrawPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [walletRes, methodsRes] = await Promise.all([
        walletApi.getWallet(),
        withdrawalApi.getMethods(),
      ]);
      setWallet(walletRes.data);
      setMethods(methodsRes.data);
      if (methodsRes.data.length > 0) {
        const def = methodsRes.data.find(m => m.is_default) || methodsRes.data[0];
        setSelectedMethod(def);
      }
    } catch (error) {
      toast.error('Failed to load withdrawal data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMethod && amount && parseFloat(amount) > 0) {
      fetchFee();
    }
  }, [selectedMethod, amount]);

  const fetchFee = async () => {
    try {
      const res = await withdrawalApi.getFees(parseFloat(amount), selectedMethod.method_id);
      setFee(res.data);
    } catch (error) {
      setFee(null);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedMethod || !amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (parseFloat(amount) > wallet.available_balance) {
      toast.error('Insufficient balance');
      return;
    }
    setIsSubmitting(true);
    try {
      await withdrawalApi.request({ method_id: selectedMethod.method_id, amount: parseFloat(amount) });
      setSuccess(true);
      toast.success('Withdrawal initiated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  if (success) {
    return (
      <AppLayout>
        <div className="min-h-screen lg:min-h-0 flex items-center justify-center p-6 lg:p-0">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6"><Check className="w-10 h-10 text-green-500" /></div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Withdrawal Initiated</h2>
            <p className="text-muted-foreground mb-6">Your withdrawal of {formatCurrency(parseFloat(amount))} is being processed.</p>
            <Button onClick={() => navigate('/home')} className="w-full bg-primary hover:bg-red-600">Back to Home</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Withdraw</h1>
        </div>

        <div className="px-6 lg:px-0 lg:grid lg:grid-cols-2 lg:gap-8 space-y-6 lg:space-y-0">
          {/* Left: Form */}
          <div className="space-y-6">
            {/* Balance */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <p className="text-muted-foreground text-sm mb-1">Available Balance</p>
              <p className="text-3xl font-bold balance-text" data-testid="withdraw-balance">{formatCurrency(wallet?.available_balance)}</p>
            </div>

            {/* Amount */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <label className="text-sm text-muted-foreground mb-2 block">Amount</label>
              <div className="flex items-center gap-2">
                <span className="text-2xl text-muted-foreground">₦</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 text-3xl font-bold bg-transparent border-none focus:outline-none"
                  data-testid="withdraw-amount"
                />
              </div>
              <div className="flex gap-2 mt-4">
                {[1000, 5000, 10000, 50000].map((a) => (
                  <button key={a} onClick={() => setAmount(a.toString())} className="px-3 py-1.5 bg-white/5 rounded-lg text-sm hover:bg-white/10 transition-colors">
                    ₦{a.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Fee Summary */}
            {fee && (
              <div className="bg-card border border-white/10 rounded-xl p-5">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span>{formatCurrency(fee.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Fee</span><span>{formatCurrency(fee.fee)}</span></div>
                  <div className="border-t border-white/10 pt-2 flex justify-between font-medium"><span>You receive</span><span className="text-green-500">{formatCurrency(fee.net_amount)}</span></div>
                  <p className="text-xs text-muted-foreground">{fee.processing_time}</p>
                </div>
              </div>
            )}

            <Button
              onClick={handleWithdraw}
              disabled={isSubmitting || !selectedMethod || !amount || parseFloat(amount) <= 0}
              className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
              data-testid="withdraw-submit"
            >
              {isSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : 'Withdraw'}
            </Button>
          </div>

          {/* Right: Methods */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Withdrawal Method</h3>
                <button className="text-sm text-primary flex items-center gap-1" onClick={() => toast.info('Add method coming soon')}>
                  <Plus className="w-4 h-4" /> Add New
                </button>
              </div>
              <div className="space-y-3">
                {methods.map((method) => {
                  const Icon = method.type === 'bank_transfer' ? Building : Smartphone;
                  return (
                    <button
                      key={method.method_id}
                      onClick={() => setSelectedMethod(method)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors text-left ${
                        selectedMethod?.method_id === method.method_id
                          ? 'border-primary bg-primary/5'
                          : 'border-white/10 bg-card hover:border-white/20'
                      }`}
                      data-testid={`method-${method.method_id}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${method.type === 'bank_transfer' ? 'bg-blue-500/20' : 'bg-green-500/20'} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${method.type === 'bank_transfer' ? 'text-blue-500' : 'text-green-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{method.provider_name}</p>
                        <p className="text-sm text-muted-foreground">{method.account_number_masked} &middot; {method.account_name}</p>
                      </div>
                      {selectedMethod?.method_id === method.method_id && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"><Check className="w-4 h-4 text-white" /></div>
                      )}
                    </button>
                  );
                })}
                {methods.length === 0 && (
                  <div className="text-center py-8 bg-card border border-white/10 rounded-xl">
                    <p className="text-muted-foreground">No withdrawal methods added yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => toast.info('Coming soon')}>Add Method</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default WithdrawPage;
